// ==========================================================================
// StarPlayer Agent
// ==========================================================================

import support from '../support';
import { toggleClass, removeElement, createElement } from '../utils/elements';
import { on, once, triggerEvent } from '../utils/events';
import is from '../utils/is';
import { silencePromise } from '../utils/promise';
import { setAspectRatio } from '../utils/style';
import { format } from '../utils/strings';
import i18n from '../utils/i18n';
import browser from '../utils/browser';
import { replaceAll } from '../utils/strings';
import ui from '../ui';
import { fetch as fetchPolyfill } from 'whatwg-fetch'
import { parseUrl } from '../utils/urls';
import Tracker from './tracker';
import { getDate } from '../utils/time';
// import { fetchToken } from '../utils/fetch';

function wsError() {
  player.error.code = i18n.get2("playerNotInstalled", player.config.starplayerErrorCode);
  //starplayer$1.cancelRequests.call(player);
  //starplayer$1.showMessage.call(player, msg);
  triggerEvent.call(player, player.media, 'error');
}

function getAgentSetupUrl() {
  if (browser.isOsx) {
    return STARPLAYER_OSX_SETUP_URL;
  }
  return STARPLAYER_AGENT_SETUP_URL;
}

function getAgentMinimumVersion() {
  if (browser.isOsx) {
    return STARPLAYER_OSX_VERSION;
  }
  return STARPLAYER_AGENT_VERSION;
}

function getVodUrl(url, sid) {
  let newUrl;

  let sep = '?';
  if (url.indexOf('?') != -1) {
    sep = '&';
  }

  if (window.location.protocol == 'https:') {
    newUrl = 'https://localhost.axissoft.co.kr:3454/{0}' + sep + 'sid={1}';
  } else {
    newUrl = 'http://127.0.0.1:3450/{0}' + sep + 'sid={1}';
  }

  return format(newUrl, url, sid);
}

function getWSUrl() {
  let url;
  if (window.location.protocol == 'https:') {
    url = 'wss://localhost.axissoft.co.kr:3453/starplayer';
  } else {
    url = 'ws://127.0.0.1:3451/starplayer';
  }
  return url;
}

function checkAgentVersion(agentVersion, seperator) {
  const wantedVersion = getAgentMinimumVersion().split(',');
  const currentVersion = agentVersion.split(seperator);
  function check(i) {
    const cv = parseInt(currentVersion[i], 10);
    const wv = parseInt(wantedVersion[i], 10);
    if (cv > wv) {
      return 1;
    } else if (cv == wv) {
      if (i == 3) return 0;
      else return check(i + 1);
    } else {
      return -1;
    }
  }
  return check(0);
}

function getLearningRecordDateStyle(date) {
  const format = (value) => `0${value}`.slice(-2);

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const mins = date.getMinutes();
  const secs = date.getSeconds();

  return `${year}/${format(month)}/${format(day)} ${format(hours)}:${format(mins)}:${format(secs)}`;
}


function getEventDateStyle(date) {
  const format = (value) => `0${value}`.slice(-2);

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const mins = date.getMinutes();
  const secs = date.getSeconds();

  return `${year}${format(month)}${format(day)}${format(hours)}${format(mins)}${format(secs)}`;
}

const starplayer = {

  showMessage(message, passive = true) {
    if (passive && this.message) {
      return Promise.reject(new Error('message already display'));
    }

    if (this.elements.message) {

      ui.togglePoster.call(this, false);

      this.elements.message.removeAttribute('hidden');
      this.elements.text.innerHTML = message;
      toggleClass(this.elements.container, this.config.classNames.messageEnabled, true);
    }
  },

  getSources() {
    if (!this.isStarPlayer) {
      return [];
    }

    const sources = Array.from(this.media.querySelectorAll('sourcex'));

    // Filter out unsupported sources (if type is specified)
    return sources.filter((source) => {
      const type = source.getAttribute('type');

      if (is.empty(type)) {
        return true;
      }

      return support.mime.call(this, type);
    });
  },

  // Get quality levels
  getQualityOptions() {
    // Whether we're forcing all options (e.g. for streaming)
    if (this.config.quality.forced) {
      return this.config.quality.options;
    }

    // Get sizes from <source> elements
    return starplayer.getSources
      .call(this)
      .map((source) => Number(source.getAttribute('size')))
      .filter(Boolean);
  },

  setup() {
    if (!this.isStarPlayer) {
      return;
    }

    const player = this;

    player.stars.tick = 0;
    player.stars.playedTimeWithSpeed = 0;
    player.stars.canceled = false;

    on.call(player, player.media, 'error', () => {

      let msg;
      if(player.media.error){
        player.error.code = player.media.error.code;
      switch (player.media.error.code) {
        case 1: // MEDIA_ERR_ABORTED:
          msg = i18n.get('mediaErrorAbort', player.config.starplayer);
          break;
        case 2: // MEDIA_ERR_NETWORK:
          msg = i18n.get('mediaErrorNetwork', player.config.starplayer);
          break;
        case 3: // MEDIA_ERR_DECODE:
          msg = i18n.get('mediaErrorDecode', player.config.starplayer);
          break;
        case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED:
          msg = i18n.get('mediaErrorSrcNotSupported', player.config.starplayer);
          break;
        default:
          msg = i18n.get('mediaErrorUnknown', player.config.starplayer);
          break;
      }
      starplayer.cancelRequests.call(player);
      starplayer.showMessage.call(player, msg);
      }
    });

    // on.call(player, player.media, 'loadstart', () => {
    //     //OPENING
    // });

    if (starplayer.watermarkEnabled.call(player)) {
      on.call(player, player.media, 'playing', () => {
        starplayer.watermarkRender.call(player);
      });

      on.call(player, player.media, 'pause ended emptied abort error waiting', () => {
        starplayer.watermarkRelease.call(player);
      });
    }

    on.call(player, player.media, 'ended abort', (event) => {
      // player.debug.log(event.type)
      const userId = player.config.starplayer.userId;
      if (is.url(player.config.starplayer.logUrl) && userId != 'ANONYMOUS') {
        clearInterval(player.stars.learningRecordTimer);
        if (player.duration != 0 && player.tracker.getPlayedTime() != 0) {
          starplayer.scmsDisconnected.call(player);
        }
      }
    });

    on.call(player, player.media, 'opened', (event) => {
      const status = event.detail.status;
      if (status == 'UnknownError') {
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('mediaErrorUnknown', player.config.starplayer));
      }

      clearInterval(player.stars.keepAliveTimer);
      player.stars.keepAliveTimer = setInterval(() => {
        starplayer.scmsKeepAlive.call(player);
      }, 1000 * player.stars.keepaliveInterval);

      const userId = player.config.starplayer.userId;
      if (is.url(player.config.starplayer.logUrl) && userId != 'ANONYMOUS') {
        player.tracker = new Tracker(player);
        clearInterval(player.stars.learningRecordTimer);
        player.stars.learningRecordTimer = setInterval(() => {
          if (player.duration != 0 && player.tracker.getPlayedTime() != 0) {
            starplayer.scmsSaveRecordInfo.call(player);
          }
        }, 1000);
      }

      if (browser.isChrome || browser.isEdge) {
        clearInterval(player.stars.browserExtensionTimer);
        player.stars.browserExtensionTimer = setInterval(() => {
          if (browser.isChrome) {
            starplayer.checkBrowserExtension.call(player, 'chrome');
          } else if (browser.isEdge) {
            starplayer.checkBrowserExtension.call(player, 'edge');
          }
        }, 1000 * 10);
      }

      starplayer.beginContentEvent.call(player);

      on.call(player, player.media, 'playing', () => {
        clearInterval(player.stars.playingContentEventTimer);
        player.stars.playingContentEventTimer = setInterval(() => {
          starplayer.ContentEvent.call(player, 'playing_content', (txt) => {
            player.playingContentEvent(txt);
          });
        }, 180000);
      });

      on.call(player, player.media, 'pause', () => {
        clearInterval(player.stars.playingContentEventTimer);
        starplayer.ContentEvent.call(player, 'playing_content', (txt) => {
          player.playingContentEvent(txt);
        });
      });

      // on.call(player, player.media, 'ended emptied abort error', () => {
      //   clearInterval(player.stars.playingContentEventTimer);
      //   starplayer.ContentEvent.call(player, 'end_content', (txt)=> {
      //     player.endContentEvent(txt);
      //   });
      // });  

    });

    on.call(player, player.media, 'keepalive', (event) => {
      const status = event.detail.status;
      if (status == 'MultipleConnections') {
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('multipleConnections', player.config.starplayer));
        player.error.code = i18n.get2("multipleConnections", player.config.starplayerErrorCode);
      } else if (status == 'DetectHardware') {
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('detectedHardware', player.config.starplayer));
        player.error.code = i18n.get2("detectedHardware", player.config.starplayerErrorCode);
      } else if (status == 'CanNotTerminated') {
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('detectedProcessHidder', player.config.starplayer));
        player.error.code = i18n.get2("CanNotTerminated", player.config.starplayerErrorCode);
      }
    });

    // Set speed options from config
    player.options.speed = player.config.speed.options; //[0.6, 0.8, 1, 1.2, 1.4, 1.6, 1.8, 2.0]

    // Set aspect ratio if fixed
    if (!is.empty(this.config.ratio)) {
      setAspectRatio.call(player);
    }

    // Quality
    Object.defineProperty(player.media, 'quality', {
      get() {
        // Get sources
        const sources = starplayer.getSources.call(player);
        const currentSrc = player.source;
        const source = sources.find((s) => currentSrc.indexOf(s.getAttribute('src')) != -1);

        // Return size, if match is found
        return source && Number(source.getAttribute('size'));
      },
      set(input) {
        if (player.quality === input && !is.empty(player.media.src)) {
          return;
        }

        // If we're using an an external handler...
        if (player.config.quality.forced && is.function(player.config.quality.onChange)) {
          player.config.quality.onChange(input);
        } else {
          // Get sources
          const sources = starplayer.getSources.call(player);
          // Get first match for requested size
          const source = sources.find((s) => Number(s.getAttribute('size')) === input);

          // No matching source found
          if (!source) {
            return;
          }

          player.elements.inputs.seek.disabled = true;
          // Get current state
          const { currentTime, paused, preload, readyState, playbackRate } = player.media;

          player.stars.contentId = source.getAttribute('contentid') || '';

          // Set new source
          const src = source.getAttribute('src');

          once.call(player, player.media, 'connection', (e) => {

            player.stars.beginLocaltime = getLearningRecordDateStyle(new Date());

            const status = e.detail.status;
            const msg = e.detail.msg;
            const sessionId = e.detail.session_id;

            if (!starplayer.checkStatus.call(player, status, msg)) {
              return;
            }

            player.stars.currentUrl = src;
            const newUrl = getVodUrl(src, sessionId);
            player.media.src = newUrl;

            // Prevent loading if preload="none" and the current source isn't loaded (#1044)
            if (preload !== 'none' || readyState) {
              // Restore time
              player.once('loadedmetadata', () => {
                // player.speed = playbackRate;
                player.elements.inputs.seek.disabled = false;
                player.speed = null;
                player.currentTime = currentTime;

                starplayer.scmsOpened.call(player);
                // Resume playing
                if (!paused) {
                  silencePromise(player.play());
                }
              });

              // Load new source
              player.media.load();
            }
          });

          starplayer.wakeupAgent.call(player, src)
        }

        // Trigger change event
        triggerEvent.call(player, player.media, 'qualitychange', false, {
          quality: input,
        });
      },
    });

    //for hls, dash
    if (player.source.indexOf('blob:', 0) == 0) {
      setTimeout(() => {
        // const sources = starplayer.getSources.call(player);
        // const src = sources[0]
        // const sources = Array.from(player.media.querySelectorAll('source'));
        let src = player.source
        const sourceElement = player.media.querySelector('source');
        if (is.element(sourceElement)) {
          src = sourceElement.getAttribute('src');
        }

        // const src = player.source;
        once.call(player, player.media, 'connection', (e) => {

          player.stars.beginLocaltime = getLearningRecordDateStyle(new Date());

          const status = e.detail.status;
          const msg = e.detail.msg;
          // const sessionId = e.detail.session_id;

          if (!starplayer.checkStatus.call(player, status, msg)) {
            return;
          }

          player.stars.currentUrl = src;
          player.elements.inputs.seek.disabled = false;
          player.speed = null;
          starplayer.scmsOpened.call(player);
        });

        starplayer.wakeupAgent.call(player, src)
      }, 1000);
    }
  },

  checkStatus(status, msg) {
    if (status != '' && status != 'Connected') {
      if (msg !== '') {
        // player.debug.log(msg);
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, msg);
        return;
      }
      if (status == 'MultipleConnections') {
        // player.debug.log(status);
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('multipleConnections', player.config.starplayer));
      } else if (status == 'BlockedUID') {
        // player.debug.log(status);
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('blockedUserId', player.config.starplayer));
      } else if (status == 'BlockedIP') {
        // player.debug.log(status);
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('blockedIp', player.config.starplayer));
      } else if (status == 'BlockedPID') {
        // player.debug.log(status);
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('blockedMachine', player.config.starplayer));
      } else if (status == 'VMACHINE') {
        // player.debug.log(status);
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('blockedVmachine', player.config.starplayer));
      } else if (status == 'RDESKTOP') {
        // player.debug.log(status);
        starplayer.cancelRequests.call(player);
        starplayer.showMessage.call(player, i18n.get('blockedRdesktop', player.config.starplayer));
      }
      return false;
    }
    return true;
  },

  wakeupAgent(src) {
    const player = this;
    if (player.config.starplayer.token.enabled) {
      const option = {
        url: player.config.starplayer.token.url,
        apiKey: player.config.starplayer.token.apiKey,
        vod: parseUrl(src).pathname,
      };

      // fetchToken(option).then((json)=>{
      //   if (!is.empty(json.api_data)) {
      //     starplayer.sendUrl.call(player, src, json.api_data);
      //     starplayer.scmsConnection.call(player);
      //   } else {
      //     starplayer.showMessage.call(player, i18n.get('tokenError', player.config.starplayer));  
      //   }
      // }).catch((e)=>{
      //   starplayer.showMessage.call(player, i18n.get('tokenError', player.config.starplayer));
      // });

      fetchPolyfill(option.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api_key': option.apiKey,
        },
        body: JSON.stringify({ vod: option.vod, })
      }).then((response) => {
        return response.json();
      }).then((json) => {
        if (!is.empty(json.api_data)) {
          clearInterval(player.stars.learningRecordTimer);
          starplayer.sendUrl.call(player, src, json.api_data);
          starplayer.scmsConnection.call(player);
        } else {
          starplayer.showMessage.call(player, i18n.get('tokenError', player.config.starplayer));
        }
      }).catch((e) => {
        starplayer.showMessage.call(player, i18n.get('tokenError', player.config.starplayer));
      });

    } else {
      clearInterval(player.stars.learningRecordTimer);
      starplayer.sendUrl.call(player, src);
      starplayer.scmsConnection.call(player);
    }
  },

  recordInfo() {
    const player = this;

    const endLocaltime = getLearningRecordDateStyle(new Date());
    const userId = unescape(encodeURIComponent(encodeURIComponent(player.config.starplayer.userId)));
    const extra = unescape(encodeURIComponent(encodeURIComponent(player.stars.contentId)));

    const info = `user_id=${userId}&player_id=${player.stars.playerId}&content_duration=${parseInt(player.duration, 10)}&current_position=${parseInt(player.currentTime, 10)}&played_time=${player.tracker.getPlayedTime()}&strong_played_time=${player.tracker.getPlayedTimeWithSpeed()}&tracker_time=${player.tracker.getTrackerTime()}&tracker_data=${player.tracker.getTracker()}&extra_data=${extra}&begin_localtime=${encodeURIComponent(player.stars.beginLocaltime)}&end_localtime=${encodeURIComponent(endLocaltime)}&content_url=${player.stars.currentUrl}`;
    // player.debug.log(info);
    return info;
  },

  sendUrl(src, token = null) {
    const player = this;
    var extra = unescape(encodeURIComponent(encodeURIComponent(player.stars.contentId)));
    const msg = {
      url: src,
      custom_log_url: player.config.starplayer.logUrl,
      custom_log_extra: extra,
      url_token: token,
    };
    player.stars.ws.send(JSON.stringify(msg));
  },

  scmsConnection() {
    const player = this;
    const msg = {
      scms: 'connection',
    };
    player.stars.ws.send(JSON.stringify(msg));
  },

  scmsOpened() {
    const player = this;
    const msg = {
      scms: 'opened',
    };
    player.stars.ws.send(JSON.stringify(msg));
  },

  scmsKeepAlive() {
    const player = this;
    const msg = {
      scms: 'keepalive',
    };
    player.stars.ws.send(JSON.stringify(msg));
  },

  scmsDisconnected() {
    const player = this;
    const info = starplayer.recordInfo.call(player);
    const msg = {
      disconnected: window.btoa(info),
    };
    player.stars.ws.send(JSON.stringify(msg));
  },

  scmsSaveRecordInfo() {
    const player = this;
    const info = starplayer.recordInfo.call(player);
    const msg = {
      history: window.btoa(info),
    }
    player.stars.ws.send(JSON.stringify(msg));
  },

  scmsSaveRecordNullInfo() {
    const player = this;
    const userId = unescape(encodeURIComponent(encodeURIComponent(player.config.starplayer.userId)));
    const msg = {
      history: window.btoa(`user_id=${userId}&player_id=${player.stars.playerId}`),
    }
    player.stars.ws.send(JSON.stringify(msg));
  },

  emergency(playstate) {
    const player = this;
    const msg = {
      emergency: playstate, //playing 1
    };
    player.stars.ws.send(JSON.stringify(msg));
  },

  //browser: chrome, edge
  checkBrowserExtension(browser) {
    const player = this;
    const msg = {
      browser_extension: browser,
    };
    player.stars.ws.send(JSON.stringify(msg));
  },

  installAgent() {
    const player = this;

    let msg = i18n.get('installAgent', player.config.starplayer);
    const url = getAgentSetupUrl();
    msg = replaceAll(msg, '{agentSetupUrl}', url);
    starplayer.showMessage.call(player, msg);
    player.stars.installTimer = setTimeout(() => {
      starplayer.checkInstalled.call(player);
    }, 1000);
  },

  updateAgent() {
    const player = this;
    let msg = i18n.get('updateAgent', player.config.starplayer);
    const url = getAgentSetupUrl();
    msg = replaceAll(msg, '{agentSetupUrl}', url);
    starplayer.showMessage.call(player, msg);
    player.stars.installTimer = setTimeout(() => {
      starplayer.checkInstalled.call(player);
    }, 1000);
  },

  checkInstalled() {
    if (!this.isStarPlayer) {
      return;
    }
    const player = this;
    player.stars.ws = new WebSocket(getWSUrl());
    const ws = player.stars.ws;
    ws.onopen = (evt) => {
      const msg = {
        config: player.config.starplayer.config,
      };
      ws.send(JSON.stringify(msg));
    };
    ws.onmessage = (evt) => {
      const jsonObj = JSON.parse(evt.data);
      if (typeof jsonObj.version !== 'undefined') {
        if (checkAgentVersion(jsonObj.version, '.') == -1) {
          // player.stars.installTimer = setTimeout(() => {
          //  starplayer.checkInstalled.call(player);
          // }, 1000);
          //player.stars.isStarPlayerInstalled = false;
          //starplayer.updateAgent.call(player);
        } else {
          setTimeout(() => {
            location.reload();
          }, 100);
        }
      }
      ws.close();
    };
    ws.onclose = (evt) => {
      player.stars.installTimer = setTimeout(() => {
        starplayer.checkInstalled.call(player);
      }, 2000);
    };
    ws.onerror = (evt) => {
      if (!player.error.code) {
        wsError();
      }
     };
  },

  connectAgent() {
    const player = this;
    if (is.object(player.stars.ws)) {
      return;
    }

    player.stars.keepaliveInterval = 60;

    return new Promise((resolve, reject) => {
      player.stars.isStarPlayerInstalled = false;
      player.stars.ws = new WebSocket(getWSUrl());
      const ws = player.stars.ws;
      ws.onopen = (evt) => {
        player.stars.isStarPlayerInstalled = true;
        clearTimeout(player.stars.canceledTimer);

        const msg = {
          //url: player.source,
          config: player.config.starplayer.config,
          referer: window.parent.location.href,
          spk_id: player.config.starplayer.spkId,
          user_id: player.config.starplayer.userId,
        };
        ws.send(JSON.stringify(msg));
      };
      ws.onmessage = (evt) => {
        const jsonObj = JSON.parse(evt.data);
        if (typeof jsonObj.version !== 'undefined') {
          if (checkAgentVersion(jsonObj.version, '.') == -1) {
            player.stars.isStarPlayerInstalled = false;
            starplayer.updateAgent.call(player);
            reject();
          } else {

            player.stars.playerId = jsonObj.player_id;
            player.stars.macAddress = jsonObj.mac_address;

            if (typeof jsonObj.keepalive_interval !== 'undefined') {
              player.stars.keepaliveInterval = jsonObj.keepalive_interval > 60 ? jsonObj.keepalive_interval : 60;
            }
            resolve();
          }
        }

        if (typeof jsonObj.method !== 'undefined') {
          if (jsonObj.method == 'keepalive') {
            triggerEvent.call(player, player.media, jsonObj.method, false, {
              status: jsonObj.status,
            });
          } else if (jsonObj.method == 'connection') {
            triggerEvent.call(player, player.media, jsonObj.method, false, {
              status: jsonObj.status,
              msg: decodeURIComponent(jsonObj.msg),
              session_id: jsonObj.session_id,
            });
          } else if (jsonObj.method == 'opened') {
            triggerEvent.call(player, player.media, jsonObj.method, false, {
              status: jsonObj.status,
            });
          } else if (jsonObj.method == 'ssn') {
            triggerEvent.call(player, player.media, jsonObj.method, false, {
              value: jsonObj.value,
            });
          } else if (jsonObj.method == 'signal') {
            if (is.url(player.config.starplayer.logUrl)) {
              const userId = player.config.starplayer.userId;
              if (jsonObj.status == 'stop' && jsonObj.value == player.config.starplayer.logUrl && userId != 'ANONYMOUS') {
                clearInterval(player.stars.learningRecordTimer);
                starplayer.scmsSaveRecordNullInfo.call(player);
                starplayer.cancelRequests.call(player);
                starplayer.showMessage.call(player, i18n.get('mediaErrorAbort', player.config.starplayer));
                return;
              }
            }

            if (jsonObj.status == 'stop' && jsonObj.value == 'DetectHardware') {
              starplayer.cancelRequests.call(player);
              starplayer.showMessage.call(player, i18n.get('detectedHardware', player.config.starplayer));
            } else if (jsonObj.status == 'stop' && jsonObj.value == 'CanNotTerminated') {
              starplayer.cancelRequests.call(player);
              starplayer.showMessage.call(player, i18n.get('detectedProcessHidder', player.config.starplayer));
            } else if (jsonObj.status == 'stop') {
              var obj = JSON.parse(jsonObj.value);
              starplayer.cancelRequests.call(player);
              starplayer.showMessage.call(
                player,
                i18n.get('detectedBrowserExtension', player.config.starplayer) +
                '<br /> ( <a href=' +
                obj.link +
                ' target="_blank">' +
                obj.module +
                '</a> )',
              );
            }
          }
          // else if (jsonObj.method == 'chrome_extension') {
          //   triggerEvent.call(player, player.media, jsonObj.method, false, {
          //     id: jsonObj.id,
          //     installed: jsonObj.installed
          //   });
          // }
        }
      };
      ws.onclose = (evt) => {
        player.debug.log('starplayer onclose');

        if (!player.stars.isStarPlayerInstalled) {
          player.stars.canceledTimer = setTimeout(() => {
            starplayer.installAgent.call(player);
            reject();
          }, 2000);
        } else {
          if (!player.stars.canceled) {
            starplayer.cancelRequests.call(player);
            player.closedWebsocketEvent();
          }
        }

      };
      ws.onerror = (evt) => {
        if (!player.error.code) {
          wsError();
        }
        player.debug.log('starplayer onerror');
      };
    });
  },

  cancelRequests() {
    if (!this.isStarPlayer) {
      return;
    }

    const player = this;
    clearTimeout(player.stars.installTimer);
    clearInterval(player.stars.keepAliveTimer);
    clearInterval(player.stars.browserExtensionTimer);
    clearInterval(player.stars.learningRecordTimer);
    clearTimeout(player.stars.canceledTimer);

    starplayer.watermarkRelease.call(player);


    if (player.stars.ws) {
      player.stars.canceled = true;
      player.stars.ws.close();
    }

    // if (this.elements.poster) {
    //   this.elements.poster.removeAttribute('hidden');
    // }
    ui.togglePoster.call(this, true);

    if (this.elements.message) {
      this.elements.message.setAttribute('hidden', '');
    }

    toggleClass(this.elements.container, this.config.classNames.messageEnabled, false);

    // Remove child sources
    removeElement(starplayer.getSources.call(this));

    // Set blank video src attribute
    // This is to prevent a MEDIA_ERR_SRC_NOT_SUPPORTED error
    // Info: http://stackoverflow.com/questions/32231579/how-to-properly-dispose-of-an-html5-video-and-close-socket-or-connection
    this.media.setAttribute('src', this.config.blankVideo);

    // Load the new empty source
    // This will cancel existing requests
    // See https://github.com/sampotts/plyr/issues/174
    this.media.load();

    // Debugging
    this.debug.log('Cancelled network requests');
  },

  watermarkEnabled() {
    const player = this;
    return player.isStarPlayer && player.isVideo && player.config.starplayer.watermark.enabled;
  },

  watermarkRender() {
    const player = this;
    player.elements.watermark = createElement('div');
    player.elements.wrapper.appendChild(player.elements.watermark);

    let interval = player.config.starplayer.watermark.positionChangeInterval;
    const intervalIsValid = is.number(interval) && interval > 0;
    interval = intervalIsValid ? interval : 5;

    let displayDuration = player.config.starplayer.watermark.displayDuration;
    const displayDurationIsValid = is.number(displayDuration) && displayDuration < interval;
    displayDuration = displayDurationIsValid ? displayDuration : interval;

    if (player.stars.watermarkTimer) {
      clearInterval(player.stars.watermarkTimer);
    }

    if (player.stars.watermarkDurationTimer) {
      clearTimeout(player.stars.watermarkDurationTimer);
    }

    player.stars.watermarkTimer = setInterval(() => {
      // if (!is.element(player.elements)) {
      //   return;
      // }

      if (!is.element(player.elements.watermark)) {
        player.elements.watermark = createElement('div');
      }

      if (!player.elements.watermark.parentNode) {
        player.elements.wrapper.appendChild(player.elements.watermark);
      }

      player.elements.watermark.innerHTML = player.config.starplayer.watermark.text;
      let style = player.elements.watermark.style;
      style.position = 'absolute';
      style.width = '100%';
      style.display = 'block';
      style.margin = '0';
      style.padding = '0';
      style.textTransform = 'uppercase';
      style.fontWeight = 'bold';

      if (browser.isAndroid || browser.isIos) {
        style.fontSize = '100%';
      } else {
        style.fontSize = '150%';
      }
      style.color = 'white';
      style.opacity = '0.4';
      style.filter = 'opacity(40%)';
      style.zIndex = '1';

      let textAlign = 'initial';
      switch (Math.floor(Math.random() * 10000) % 3) {
        case 0:
          textAlign = 'left';
          break;
        case 1:
          textAlign = 'center';
          break;
        case 2:
          textAlign = 'right';
          break;
      }
      style.textAlign = textAlign;

      let top;
      switch (Math.floor(Math.random() * 10000) % 3) {
        case 0:
          top = 0;
          break;
        case 1:
          top = 45;
          break;
        case 2:
          top = 90;
          player.elements.watermark.innerHTML = getDate(new Date());
          break;
      }
      style.top = `${top}%`;

      player.stars.watermarkDurationTimer = setTimeout(() => {
        style.display = 'none';
      }, displayDuration * 1000 - 100);
    }, interval * 1000);
  },

  watermarkRelease() {
    const player = this;
    if (player.elements.watermark) {
      player.elements.watermark.remove();
    }

    if (player.stars.watermarkTimer) {
      clearInterval(player.stars.watermarkTimer);
    }

    if (player.stars.watermarkDurationTimer) {
      clearTimeout(player.stars.watermarkDurationTimer);
    }
  },

  beginContentEvent() {
    const userId = player.config.starplayer.userId;
    if (is.url(player.config.starplayer.eventUrl) && userId != 'ANONYMOUS') {
      let data = new FormData();
      data.append('event', "begin_content");
      data.append('user_id', userId);
      data.append('device_id', player.stars.playerId);
      data.append('os_version', browser.isOsx ? "macOS" : "windows");
      data.append('app_version', "0");
      data.append('content_id', player.stars.contentId);
      data.append('content_url', encodeURIComponent(player.stars.currentUrl));
      data.append('play_type', "download");
      data.append('state', "normal");
      data.append('license', player.config.starplayer.app.license);
      data.append('date', getEventDateStyle(new Date()));
      data.append('online', "yes");
      //data.append('format', player.config.starplayer.responseType);

      fetchPolyfill(player.config.starplayer.eventUrl, {
        method: 'POST',
        headers: {
          'format': player.config.starplayer.responseType,
        },
        body: data
      }).then((response) => {
        return response.text()
      }).then((str) => {
        player.beginContentEvent(str);
      }).catch((e) => {
        player.debug.log(e);
      });
    }
  },

  ContentEvent(event, callback) {
    const userId = player.config.starplayer.userId;
    if (is.url(player.config.starplayer.eventUrl) && userId != 'ANONYMOUS') {
      let data = new FormData();
      data.append('event', event);
      data.append('user_id', userId);
      data.append('device_id', player.stars.playerId);
      data.append('os_version', browser.isOsx ? "macOS" : "windows");
      data.append('app_version', "0");
      data.append('content_id', player.stars.contentId);
      data.append('content_url', encodeURIComponent(player.stars.currentUrl));
      data.append('play_type', "download");
      data.append('license', player.config.starplayer.app.license);
      let time = player.tracker.getPlayedTime();
      //let playedTime = time - player.stars.playedTime;
      //player.stars.playedTime = time;
      data.append('play_time', time);
      data.append('play_time2', time * 1000);
      let currentTick = new Date().getTime();
      if (player.stars.tick == 0) {
        player.stars.tick = currentTick;
      }
      const diff = currentTick - player.stars.tick;
      player.stars.tick = currentTick;
      data.append('latest_playtime', parseInt(diff / 1000, 10));
      data.append('latest_playtime2', diff);
      const speed = player.speed;
      data.append('latest_ratio_playtime', parseInt(diff * speed.toFixed(1) / 1000, 10));
      data.append('content_duration', parseInt(player.duration, 10));
      data.append('current_position', parseInt(player.currentTime, 10));

      data.append('date', getEventDateStyle(new Date()));
      data.append('online', "yes");
      data.append('play_ratio', speed.toFixed(1));
      //data.append('format', player.config.starplayer.responseType);

      fetchPolyfill(player.config.starplayer.eventUrl, {
        method: 'POST',
        headers: {
          'format': player.config.starplayer.responseType,
        },
        body: data
      }).then((response) => {
        return response.text()
      }).then((str) => {
        //player.playingContentEvent(str);
        if (is.function(callback)) {
          callback(str);
        }
      }).catch((e) => {
        player.debug.log(e);
      });
    }
  },
};

export default starplayer;
